import { supabase } from './supabaseClient';

/**
 * Parse CSV content into an array of objects
 * @param {string} csvContent - The CSV content to parse
 * @returns {Object} - The parsed data with headers and rows
 */
export const parseCSV = (csvContent) => {
  try {
    console.log(`CSV Parsing: Starting to parse CSV file of ${csvContent.length} characters`);
    
    // Log a preview of the content (first 100 chars)
    const contentPreview = csvContent.substring(0, 100).replace(/\n/g, '\\n');
    console.log(`CSV Parsing: Content preview: ${contentPreview}...`);
    
    // Normalize line endings and split into lines
    const lines = csvContent.replace(/\r\n/g, '\n').split('\n');
    console.log(`CSV Parsing: Found ${lines.length} lines in the file`);
    
    // Get the headers (first line)
    const headers = parseCSVLine(lines[0]);
    console.log(`CSV Parsing: Detected headers (${headers.length}):`, headers);
    
    // Map the required fields to their index positions
    const fieldMapping = {
      orderCreatedDate: headers.findIndex(h => h.trim() === 'ORDER CREATED DATE'),
      logistixaiOrder: headers.findIndex(h => h.trim() === 'LOGISTIXAI ORDER #'),
      scheduledDate: headers.findIndex(h => h.trim() === 'SCHEDULED DATE'),
      orderStatus: headers.findIndex(h => h.trim() === 'ORDER STATUS'),
      customerAddress: headers.findIndex(h => h.trim() === 'CUSTOMER ADDRESS 1'),
      customerState: headers.findIndex(h => h.trim() === 'CUSTOMER STATE'),
      assignedServiceProvider: headers.findIndex(h => h.trim() === 'ASSIGNED SERVICE PROVIDER'),
      assignedResourceName: headers.findIndex(h => h.trim() === 'ASSIGNED RESOURCE NAME'),
      customer: headers.findIndex(h => h.trim() === 'CUSTOMER'),
      customerCity: headers.findIndex(h => h.trim() === 'CUSTOMER CITY'),
      destinationPostalCode: headers.findIndex(h => h.trim() === 'DESTINATION POSTAL CODE'),
    };
    console.log('CSV Parsing: Field mapping results:', fieldMapping);
    
    // Validate the CSV structure
    const requiredFields = ['orderCreatedDate', 'logistixaiOrder', 'orderStatus', 
      'customerAddress', 'customerState', 'customer', 'customerCity', 'destinationPostalCode'];
    
    const missingFields = requiredFields.filter(field => fieldMapping[field] === -1);
    
    if (missingFields.length > 0) {
      console.error(`CSV Parsing: Missing required fields: ${missingFields.join(', ')}`);
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }
    
    console.log('CSV Parsing: All required fields are present');
    
    // Parse the rows
    console.log(`CSV Parsing: Starting to parse ${lines.length - 1} data rows`);
    const rows = lines.slice(1)
      .filter(line => line.trim() !== '') // Skip empty lines
      .map((line, index) => {
        if (index < 2 || index === lines.length - 2) {
          console.log(`CSV Parsing: Processing row ${index + 1}/${lines.length - 1}: ${line.substring(0, 50)}...`);
        }
        
        const values = parseCSVLine(line);
        
        if (values.length !== headers.length) {
          console.warn(`CSV Parsing: Row ${index + 1} has ${values.length} values, but there are ${headers.length} headers`);
        }
        
        return {
          orderCreatedDate: values[fieldMapping.orderCreatedDate] || '',
          logistixaiOrder: values[fieldMapping.logistixaiOrder] || '',
          scheduledDate: values[fieldMapping.scheduledDate] || null,
          orderStatus: values[fieldMapping.orderStatus] || '',
          customerAddress: values[fieldMapping.customerAddress] || '',
          customerState: values[fieldMapping.customerState] || '',
          assignedServiceProvider: 
            fieldMapping.assignedServiceProvider !== -1 ? values[fieldMapping.assignedServiceProvider] : null,
          assignedResourceName: 
            fieldMapping.assignedResourceName !== -1 ? values[fieldMapping.assignedResourceName] : null,
          customer: values[fieldMapping.customer] || '',
          customerCity: values[fieldMapping.customerCity] || '',
          destinationPostalCode: values[fieldMapping.destinationPostalCode] || '',
        };
      });
    
    // Log sample of parsed data (first row)
    if (rows.length > 0) {
      console.log('CSV Parsing: Sample of first parsed row:', rows[0]);
    }
    
    console.log(`CSV Parsing: Successfully parsed ${rows.length} rows`);
    
    return {
      valid: true,
      rows,
      totalCount: rows.length
    };
  } catch (error) {
    console.error('Error parsing CSV:', error);
    console.error('CSV Parsing: Failed with error:', error.message, error.stack);
    return {
      valid: false,
      error: error.message,
      rows: [],
      totalCount: 0
    };
  }
};

/**
 * Parse a CSV line handling quoted fields with commas
 * @param {string} line - The CSV line to parse
 * @returns {Array} - Array of values
 */
const parseCSVLine = (line) => {
  if (!line) {
    console.warn('CSV Line Parsing: Empty line provided');
    return [];
  }
  
  const result = [];
  let inQuotes = false;
  let currentValue = '';
  let i = 0;
  
  const linePreview = line.length > 50 ? `${line.substring(0, 50)}...` : line;
  console.debug(`CSV Line Parsing: Processing line: ${linePreview}`);
  
  while (i < line.length) {
    const char = line[i];
    
    if (char === '"') {
      if (i + 1 < line.length && line[i + 1] === '"') {
        // Handle escaped quotes (two consecutive quotes)
        console.debug(`CSV Line Parsing: Found escaped quote at position ${i}`);
        currentValue += '"';
        i += 2;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        if (inQuotes) {
          console.debug(`CSV Line Parsing: Started quoted field at position ${i}`);
        } else {
          console.debug(`CSV Line Parsing: Ended quoted field at position ${i}`);
        }
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      console.debug(`CSV Line Parsing: Field complete: "${currentValue}"`);
      result.push(currentValue.trim());
      currentValue = '';
      i++;
    } else {
      // Add to current value
      currentValue += char;
      i++;
    }
  }
  
  // Add the last field
  console.debug(`CSV Line Parsing: Final field: "${currentValue}"`);
  result.push(currentValue.trim());
  
  if (inQuotes) {
    console.warn('CSV Line Parsing: Warning - Unclosed quotes in line');
  }
  
  console.debug(`CSV Line Parsing: Extracted ${result.length} fields`);
  return result;
};

/**
 * Check if a date is in the future
 * @param {string} dateString - Date string to check
 * @returns {boolean} - True if date is in the future
 */
export const isFutureDate = (dateString) => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const result = date >= today;
    console.debug(`Date Check: "${dateString}" is ${result ? 'in the future' : 'not in the future'}`);
    return result;
  } catch (error) {
    console.error('Error checking future date:', error);
    return false;
  }
};

/**
 * Process CSV data and store in Supabase
 * @param {Array} rows - Parsed CSV rows
 * @param {string} projectionId - The ID of the projection
 * @returns {Object} - Processing results
 */
export const processProjectionData = async (rows, projectionId) => {
  console.log(`Projection Processing: Starting for projection ID ${projectionId}`);
  console.log(`Projection Processing: Input data contains ${rows?.length || 0} rows`);
  
  if (!rows || rows.length === 0 || !projectionId) {
    console.error('Projection Processing: Invalid input data');
    return { success: false, error: 'Invalid input data' };
  }
  
  try {
    // Filter rows for future scheduled dates and required order status
    console.log('Projection Processing: Filtering for future scheduled dates and valid order status');
    const filteredRows = rows.filter(row => {
      // Only include rows with "Open" or "Scheduled" status
      const validStatus = row.orderStatus === "Open" || row.orderStatus === "Scheduled";
      const isFutureScheduledDate = row.scheduledDate ? isFutureDate(row.scheduledDate) : false;
      
      if (!validStatus) {
        console.debug(`Projection Processing: Skipping row with status "${row.orderStatus}" - only "Open" or "Scheduled" are allowed`);
      }
      
      return validStatus && isFutureScheduledDate;
    });
    
    console.log(`Projection Processing: Found ${filteredRows.length} valid rows (future dates with "Open" or "Scheduled" status) out of ${rows.length} total rows`);
    
    // Count unique locations by address
    const uniqueLocations = new Set();
    filteredRows.forEach(row => {
      if (row.customerAddress) {
        uniqueLocations.add(row.customerAddress.trim());
      }
    });
    
    const uniqueLocationCount = uniqueLocations.size;
    console.log(`Projection Processing: Identified ${uniqueLocationCount} unique locations from ${filteredRows.length} rows`);
    
    // Format the data for insertion into projection_locations table
    console.log('Projection Processing: Formatting data for insertion');
    const locationsToInsert = filteredRows.map(row => ({
      projection_id: projectionId,
      order_created_date: row.orderCreatedDate,
      logistixai_order_number: row.logistixaiOrder,
      scheduled_date: row.scheduledDate,
      order_status: row.orderStatus,
      customer_address: row.customerAddress,
      customer_state: row.customerState,
      assigned_service_provider: row.assignedServiceProvider,
      assigned_resource_name: row.assignedResourceName,
      customer: row.customer,
      customer_city: row.customerCity,
      destination_postal_code: row.destinationPostalCode,
      is_future: true
    }));
    
    // Log sample data
    if (locationsToInsert.length > 0) {
      console.log('Projection Processing: Sample of first formatted location:', locationsToInsert[0]);
    }
    
    // Insert the data in batches (Supabase has limits on batch size)
    const batchSize = 100;
    const batches = [];
    
    for (let i = 0; i < locationsToInsert.length; i += batchSize) {
      batches.push(locationsToInsert.slice(i, i + batchSize));
    }
    
    console.log(`Projection Processing: Inserting data in ${batches.length} batches of up to ${batchSize} records`);
    
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      console.log(`Projection Processing: Inserting batch ${i + 1}/${batches.length} (${batch.length} records)`);
      
      const { error } = await supabase
        .from('projection_locations')
        .insert(batch);
      
      if (error) {
        console.error(`Projection Processing: Error inserting batch ${i + 1}:`, error);
        throw error;
      }
      
      console.log(`Projection Processing: Successfully inserted batch ${i + 1}`);
    }
    
    // Calculate projections
    console.log('Projection Processing: Calculating projections');
    const projectionResults = await calculateProjections(projectionId, filteredRows);
    console.log('Projection Processing: Calculation results:', projectionResults);
    
    // Update the projection record with the calculated results
    console.log('Projection Processing: Updating projection record with results');
    const { error } = await supabase
      .from('projections')
      .update({
        status: 'processed',
        total_locations: uniqueLocationCount,
        total_projected_revenue: projectionResults.totalRevenue,
        total_projected_expense: projectionResults.totalExpense,
        total_projected_margin: projectionResults.totalMargin
      })
      .eq('id', projectionId);
    
    if (error) {
      console.error('Projection Processing: Error updating projection record:', error);
      throw error;
    }
    
    // Create default scenario (100% completed)
    console.log('Projection Processing: Creating default scenario');
    const { error: scenarioError } = await supabase
      .from('projection_scenarios')
      .insert([{
        projection_id: projectionId,
        name: 'Default Scenario',
        percent_completed: 100,
        percent_refused: 0,
        total_locations: uniqueLocationCount,
        completed_locations: uniqueLocationCount,
        refused_locations: 0,
        total_revenue: projectionResults.totalRevenue,
        total_expense: projectionResults.totalExpense,
        total_margin: projectionResults.totalMargin
      }]);
    
    if (scenarioError) {
      console.error('Projection Processing: Error creating default scenario:', scenarioError);
      throw scenarioError;
    }
    
    console.log('Projection Processing: Successfully completed');
    return {
      success: true,
      totalLocations: uniqueLocationCount,
      totalRevenue: projectionResults.totalRevenue,
      totalExpense: projectionResults.totalExpense,
      totalMargin: projectionResults.totalMargin
    };
  } catch (error) {
    console.error('Error processing projection data:', error);
    console.error('Projection Processing: Failed with error:', error.message, error.stack);
    
    // Update the projection record with error status
    console.log('Projection Processing: Updating projection record with error status');
    await supabase
      .from('projections')
      .update({
        status: 'error',
        error_message: error.message
      })
      .eq('id', projectionId);
    
    return { success: false, error: error.message };
  }
};

/**
 * Find provider in rate table by service provider name or resource name
 * @param {string} serviceProvider - The assigned service provider name
 * @param {string} resourceName - The assigned resource name
 * @param {Array} providers - List of providers from the rate table
 * @returns {Object} - The found provider object or null
 */
const findProvider = (serviceProvider, resourceName, providers) => {
  if (!providers || (!serviceProvider && !resourceName)) return null;
  
  // Clean and normalize names for comparison
  const normalizeString = (str) => {
    if (!str) return '';
    return str.toLowerCase().trim()
      .replace(/\s+/g, ' ')  // Normalize whitespace
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ''); // Remove punctuation
  };
  
  const cleanServiceProvider = normalizeString(serviceProvider);
  const cleanResourceName = normalizeString(resourceName);
  
  console.debug(`Provider Lookup: Searching for "${serviceProvider || ''}" / "${resourceName || ''}" (normalized to "${cleanServiceProvider}" / "${cleanResourceName}")`);
  console.debug(`Provider Lookup: Available providers: ${providers.map(p => p.name).join(', ')}`);
  
  // Try exact match on serviceProvider
  let provider = providers.find(p => normalizeString(p.name) === cleanServiceProvider);
  
  if (provider) {
    console.debug(`Provider Lookup: Found exact match on service provider: ${provider.name}`);
    return provider;
  }
  
  // Try exact match on resourceName
  if (resourceName) {
    provider = providers.find(p => normalizeString(p.name) === cleanResourceName);
    
    if (provider) {
      console.debug(`Provider Lookup: Found exact match on resource name: ${provider.name}`);
      return provider;
    }
  }
  
  // Try partial word matching
  const getWords = str => str.split(/\s+/).filter(word => word.length > 2);
  const serviceProviderWords = getWords(cleanServiceProvider);
  const resourceNameWords = getWords(cleanResourceName);
  
  console.debug(`Provider Lookup: Trying word matching with words: ${serviceProviderWords.join(', ')} / ${resourceNameWords.join(', ')}`);
  
  // Find provider with the most word matches
  let bestMatch = null;
  let maxMatches = 0;
  
  providers.forEach(p => {
    const providerWords = getWords(normalizeString(p.name));
    let matches = 0;
    
    // Count matching words
    serviceProviderWords.forEach(word => {
      if (providerWords.some(pWord => pWord.includes(word) || word.includes(pWord))) {
        matches++;
      }
    });
    
    resourceNameWords.forEach(word => {
      if (providerWords.some(pWord => pWord.includes(word) || word.includes(pWord))) {
        matches++;
      }
    });
    
    if (matches > maxMatches) {
      maxMatches = matches;
      bestMatch = p;
    }
  });
  
  if (bestMatch && maxMatches > 0) {
    console.debug(`Provider Lookup: Found partial match: ${bestMatch.name} with ${maxMatches} word matches`);
    return bestMatch;
  }
  
  // No match found, but let's try one more thing - simple substring matching as a fallback
  provider = providers.find(p => {
    const providerName = normalizeString(p.name);
    return (cleanServiceProvider && (providerName.includes(cleanServiceProvider) || 
                                      cleanServiceProvider.includes(providerName))) ||
           (cleanResourceName && (providerName.includes(cleanResourceName) || 
                                  cleanResourceName.includes(providerName)));
  });
  
  if (provider) {
    console.debug(`Provider Lookup: Found substring match: ${provider.name}`);
    return provider;
  }
  
  console.warn(`Provider Lookup: No match found for "${serviceProvider || ''}" / "${resourceName || ''}"`);
  return null;
};

/**
 * Calculate projections based on the provided data
 * @param {string} projectionId - The ID of the projection
 * @param {Array} rows - Parsed CSV rows
 * @returns {Object} - Calculation results
 */
export const calculateProjections = async (projectionId, rows) => {
  console.log(`Projection Calculation: Starting for projection ID ${projectionId}`);
  console.log(`Projection Calculation: Input data contains ${rows?.length || 0} rows`);
  
  if (!rows || rows.length === 0) {
    console.warn('Projection Calculation: No rows to calculate, returning zero values');
    return {
      totalRevenue: 0,
      totalExpense: 0,
      totalMargin: 0
    };
  }
  
  try {
    // Get the rate table data for providers
    console.log('Projection Calculation: Fetching provider rate table');
    const { data: providers, error } = await supabase
      .from('providers')
      .select('*');
    
    if (error) {
      console.error('Projection Calculation: Error fetching providers:', error);
      throw error;
    }
    
    console.log(`Projection Calculation: Retrieved ${providers?.length || 0} providers`);
    
    // Constants based on PRD
    const REVENUE_PER_COMPLETED_LOCATION = 300; // $300 per location
    console.log(`Projection Calculation: Using revenue per location: $${REVENUE_PER_COMPLETED_LOCATION}`);
    
    // Group rows by location address to count unique locations
    const locationMap = new Map();
    
    // Process each row to group by location and track units (LogistixAI order numbers)
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (i === 0 || i === rows.length - 1 || i % Math.floor(rows.length / 10) === 0) {
        console.log(`Projection Calculation: Processing row ${i + 1}/${rows.length}`);
      }
      
      // Use address as unique location identifier
      const locationKey = row.customerAddress?.trim();
      if (!locationKey) continue;
      
      if (!locationMap.has(locationKey)) {
        locationMap.set(locationKey, {
          address: locationKey,
          units: new Set(), // Use Set to track unique order numbers
          serviceProvider: row.assignedServiceProvider,
          resourceName: row.assignedResourceName,
          state: row.customerState,
          city: row.customerCity,
          postalCode: row.destinationPostalCode,
          hasAssignedProvider: !!row.assignedServiceProvider || !!row.assignedResourceName
        });
      }
      
      // Add order number to the location's units
      if (row.logistixaiOrder) {
        locationMap.get(locationKey).units.add(row.logistixaiOrder);
      }
    }
    
    console.log(`Projection Calculation: Identified ${locationMap.size} unique locations`);
    
    // Calculate revenue based on unique location count
    const uniqueLocationCount = locationMap.size;
    const totalRevenue = uniqueLocationCount * REVENUE_PER_COMPLETED_LOCATION;
    console.log(`Projection Calculation: Base revenue calculation: ${uniqueLocationCount} locations × $${REVENUE_PER_COMPLETED_LOCATION} = $${totalRevenue}`);
    
    // Calculate expense based on provider rates for each location
    let totalExpense = 0;
    let providerExpenses = {}; // Track expenses by provider for debugging
    
    for (const [locationKey, locationData] of locationMap.entries()) {
      // Only calculate expense if there's a service provider or resource name
      if (locationData.hasAssignedProvider) {
        // Find the provider in the rate table using our helper function
        const provider = findProvider(
          locationData.serviceProvider, 
          locationData.resourceName, 
          providers
        );
        
        if (provider) {
          const unitCount = locationData.units.size || 1;
          let locationExpense = 0;
          
          // If we have a per_location rate, use that
          if (provider.per_location && parseFloat(provider.per_location) > 0) {
            locationExpense = parseFloat(provider.per_location);
          } 
          // Check for per_board rate
          else if (provider.per_board && parseFloat(provider.per_board) > 0) {
            locationExpense = parseFloat(provider.per_board) * unitCount;
          }
          // Otherwise use the appropriate unit rate
          else {
            // Use the appropriate rate based on unit count if available
            if (unitCount >= 10 && provider.rate_10_plus_units && parseFloat(provider.rate_10_plus_units) > 0) {
              locationExpense = parseFloat(provider.rate_10_plus_units) * unitCount;
            }
            else if (unitCount >= 6 && provider.rate_6_9_units && parseFloat(provider.rate_6_9_units) > 0) {
              locationExpense = parseFloat(provider.rate_6_9_units) * unitCount;
            }
            else if (unitCount >= 2 && provider.rate_2_5_units && parseFloat(provider.rate_2_5_units) > 0) {
              locationExpense = parseFloat(provider.rate_2_5_units) * unitCount;
            }
            else if (provider.rate_1_unit && parseFloat(provider.rate_1_unit) > 0) {
              locationExpense = parseFloat(provider.rate_1_unit) * unitCount;
            }
          }
          
          // Track expenses by provider for debugging
          if (!providerExpenses[provider.name]) {
            providerExpenses[provider.name] = {
              count: 0,
              totalExpense: 0
            };
          }
          providerExpenses[provider.name].count++;
          providerExpenses[provider.name].totalExpense += locationExpense;
          
          totalExpense += locationExpense;
        } else {
          console.warn(`Projection Calculation: Provider not found in rate table for location "${locationKey}" with provider "${locationData.serviceProvider}" or resource "${locationData.resourceName}"`);
        }
      } else {
        console.log(`Projection Calculation: No provider assigned for location "${locationKey}", assuming $0 expense`);
      }
    }
    
    // Log provider expense summary for debugging
    console.log('Projection Calculation: Provider expenses summary:');
    Object.entries(providerExpenses).forEach(([providerName, data]) => {
      console.log(`  ${providerName}: ${data.count} locations, $${data.totalExpense.toFixed(2)} total expense`);
    });
    
    // Calculate margin
    const totalMargin = totalRevenue - totalExpense;
    
    console.log('Projection Calculation: Results:', {
      totalRevenue,
      totalExpense,
      totalMargin
    });
    
    return {
      totalRevenue,
      totalExpense,
      totalMargin
    };
  } catch (error) {
    console.error('Error calculating projections:', error);
    console.error('Projection Calculation: Failed with error:', error.message, error.stack);
    throw error;
  }
};

/**
 * Calculate a projection scenario with adjusted percentages
 * @param {string} projectionId - The ID of the projection
 * @param {number} percentCompleted - Percentage of orders completed
 * @param {number} percentRefused - Percentage of orders refused
 * @returns {Object} - Calculation results for the scenario
 */
export const calculateScenario = async (projectionId, percentCompleted, percentRefused) => {
  try {
    if (!projectionId) {
      throw new Error('Projection ID is required');
    }
    
    // Get the projection data
    const { data: projection, error: projectionError } = await supabase
      .from('projections')
      .select('*')
      .eq('id', projectionId)
      .single();
    
    if (projectionError) throw projectionError;
    
    // Get the provider rate table
    const { data: providers, error: providersError } = await supabase
      .from('providers')
      .select('*');
    
    if (providersError) throw providersError;
    
    // Get the location data for this projection
    const { data: locations, error: locationsError } = await supabase
      .from('projection_locations')
      .select('*')
      .eq('projection_id', projectionId);
    
    if (locationsError) throw locationsError;
    
    if (!locations || locations.length === 0) {
      throw new Error('No location data found for this projection');
    }
    
    // Constants from PRD
    const REVENUE_PER_COMPLETED_LOCATION = 300; // $300 per location
    const REVENUE_PER_REFUSAL_UNIT = 50; // $50 per refusal unit, not per location
    
    // Group locations by address to handle locations vs units correctly
    const locationMap = new Map();
    
    // Process each location record to group by address
    for (const location of locations) {
      const locationKey = location.customer_address?.trim();
      if (!locationKey) continue;
      
      if (!locationMap.has(locationKey)) {
        locationMap.set(locationKey, {
          address: locationKey,
          units: new Set(), // Track unique order numbers
          serviceProvider: location.assigned_service_provider,
          resourceName: location.assigned_resource_name,
          state: location.customer_state,
          city: location.customer_city,
          postalCode: location.destination_postal_code,
          hasAssignedProvider: !!location.assigned_service_provider || !!location.assigned_resource_name
        });
      }
      
      // Add order number to the location's units
      if (location.logistixai_order_number) {
        locationMap.get(locationKey).units.add(location.logistixai_order_number);
      }
    }
    
    // Calculate completed and refused counts
    const totalUniqueLocations = locationMap.size;
    const completedLocations = Math.round(totalUniqueLocations * (percentCompleted / 100));
    const refusedLocations = Math.round(totalUniqueLocations * (percentRefused / 100));
    
    // Count total units across all locations for refusal calculation
    const totalUnits = Array.from(locationMap.values()).reduce((sum, location) => sum + location.units.size, 0);
    // Calculate the average units per location to apply to refusals
    const avgUnitsPerLocation = totalUnits / totalUniqueLocations || 1;
    // Calculate total units for completed and refused based on their location counts
    const completedUnits = Math.round(completedLocations * avgUnitsPerLocation);
    const refusedUnits = Math.round(refusedLocations * avgUnitsPerLocation);
    
    // Calculate revenue
    const completedRevenue = completedLocations * REVENUE_PER_COMPLETED_LOCATION;
    const refusalRevenue = refusedUnits * REVENUE_PER_REFUSAL_UNIT;
    const totalRevenue = completedRevenue + refusalRevenue;
    
    // Calculate expenses
    let totalExpense = 0;
    let providerExpenses = {}; // Track expenses by provider for debugging
    
    // Convert the map to an array of location objects and split for completed and refused
    const locationArray = Array.from(locationMap.values());
    const completedLocationsList = locationArray.slice(0, completedLocations);
    
    // Calculate expenses for completed locations
    for (const locationData of completedLocationsList) {
      if (locationData.hasAssignedProvider) {
        // Find the provider in the rate table
        const provider = findProvider(
          locationData.serviceProvider,
          locationData.resourceName,
          providers
        );
        
        if (provider) {
          const unitCount = locationData.units.size || 1;
          let locationExpense = 0;
          
          // If we have a per_location rate, use that
          if (provider.per_location && parseFloat(provider.per_location) > 0) {
            locationExpense = parseFloat(provider.per_location);
          } 
          // Check for per_board rate
          else if (provider.per_board && parseFloat(provider.per_board) > 0) {
            locationExpense = parseFloat(provider.per_board) * unitCount;
          }
          // Otherwise use the appropriate unit rate
          else {
            // Use the appropriate rate based on unit count if available
            if (unitCount >= 10 && provider.rate_10_plus_units && parseFloat(provider.rate_10_plus_units) > 0) {
              locationExpense = parseFloat(provider.rate_10_plus_units) * unitCount;
            }
            else if (unitCount >= 6 && provider.rate_6_9_units && parseFloat(provider.rate_6_9_units) > 0) {
              locationExpense = parseFloat(provider.rate_6_9_units) * unitCount;
            }
            else if (unitCount >= 2 && provider.rate_2_5_units && parseFloat(provider.rate_2_5_units) > 0) {
              locationExpense = parseFloat(provider.rate_2_5_units) * unitCount;
            }
            else if (provider.rate_1_unit && parseFloat(provider.rate_1_unit) > 0) {
              locationExpense = parseFloat(provider.rate_1_unit) * unitCount;
            }
          }
          
          // Track expenses by provider for debugging
          if (!providerExpenses[provider.name]) {
            providerExpenses[provider.name] = {
              count: 0,
              totalExpense: 0
            };
          }
          providerExpenses[provider.name].count++;
          providerExpenses[provider.name].totalExpense += locationExpense;
          
          totalExpense += locationExpense;
        }
      }
    }
    
    // Log provider expense summary for debugging
    console.log('Scenario Calculation: Provider expenses summary:');
    Object.entries(providerExpenses).forEach(([providerName, data]) => {
      console.log(`  ${providerName}: ${data.count} locations, $${data.totalExpense.toFixed(2)} total expense`);
    });
    
    // We don't add refusal expenses because Coca Cola doesn't pay for refusals
    // This was a bug in the original code
    
    // Calculate margin
    const totalMargin = totalRevenue - totalExpense;
    
    return {
      totalLocations: totalUniqueLocations,
      completedLocations,
      refusedLocations,
      totalUnits,
      completedUnits,
      refusedUnits,
      totalRevenue,
      completedRevenue,
      refusalRevenue,
      totalExpense,
      totalMargin
    };
  } catch (error) {
    console.error('Error calculating scenario:', error);
    throw error;
  }
};

/**
 * Generate and save a scenario for a projection
 * @param {string} projectionId - The ID of the projection
 * @param {string} name - The name of the scenario
 * @param {number} percentCompleted - Percentage of orders completed
 * @param {number} percentRefused - Percentage of orders refused
 * @returns {Object} - The created scenario
 */
export const saveScenario = async (projectionId, name, percentCompleted, percentRefused) => {
  try {
    // Calculate the scenario metrics
    const scenarioResults = await calculateScenario(projectionId, percentCompleted, percentRefused);
    
    // Create the scenario record
    const { data, error } = await supabase
      .from('projection_scenarios')
      .insert([{
        projection_id: projectionId,
        name,
        percent_completed: percentCompleted,
        percent_refused: percentRefused,
        total_locations: scenarioResults.totalLocations,
        completed_locations: scenarioResults.completedLocations,
        refused_locations: scenarioResults.refusedLocations,
        total_units: scenarioResults.totalUnits,
        completed_units: scenarioResults.completedUnits,
        refused_units: scenarioResults.refusedUnits,
        total_revenue: scenarioResults.totalRevenue,
        completed_revenue: scenarioResults.completedRevenue,
        refusal_revenue: scenarioResults.refusalRevenue,
        total_expense: scenarioResults.totalExpense,
        total_margin: scenarioResults.totalMargin
      }])
      .select();
    
    if (error) throw error;
    
    return {
      success: true,
      scenario: data[0],
      ...scenarioResults
    };
  } catch (error) {
    console.error('Error saving scenario:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate CSV export data from a projection scenario
 * @param {string} projectionId - The ID of the projection
 * @param {string} scenarioId - The ID of the scenario
 * @returns {string} - CSV content
 */
export const generateCSVExport = async (projectionId, scenarioId) => {
  try {
    // Get the projection data
    const { data: projection, error: projectionError } = await supabase
      .from('projections')
      .select('*')
      .eq('id', projectionId)
      .single();
    
    if (projectionError) throw projectionError;
    
    // Get the scenario data
    const { data: scenario, error: scenarioError } = await supabase
      .from('projection_scenarios')
      .select('*')
      .eq('id', scenarioId)
      .single();
    
    if (scenarioError) throw scenarioError;
    
    // Get the location data
    const { data: locations, error: locationsError } = await supabase
      .from('projection_locations')
      .select('*')
      .eq('projection_id', projectionId);
    
    if (locationsError) throw locationsError;
    
    // Generate CSV header
    let csvContent = 'Projection Name,Date Generated,Scenario Name,Total Locations,Total Units,Completed %,Refusal %,Completed Revenue,Refusal Revenue,Total Revenue,Total Expense,Total Margin\n';
    
    // Add scenario summary
    csvContent += `${projection.file_name},${new Date().toISOString()},${scenario.name},${scenario.total_locations},`;
    csvContent += `${scenario.total_units || scenario.total_locations},`; // Fallback to locations if units not available
    csvContent += `${scenario.percent_completed}%,${scenario.percent_refused}%,`;
    csvContent += `$${(scenario.completed_revenue || (scenario.total_revenue * scenario.percent_completed / 100)).toFixed(2)},`;
    csvContent += `$${(scenario.refusal_revenue || (scenario.total_revenue * scenario.percent_refused / 100)).toFixed(2)},`;
    csvContent += `$${scenario.total_revenue.toFixed(2)},$${scenario.total_expense.toFixed(2)},$${scenario.total_margin.toFixed(2)}\n\n`;
    
    // Add location details header
    csvContent += 'Order #,Scheduled Date,Address,City,State,ZIP,Provider,Resource,Status\n';
    
    // Group location data by address to handle unique locations with multiple units
    const locationMap = new Map();
    
    for (const location of locations) {
      const locationKey = location.customer_address?.trim();
      if (!locationKey) continue;
      
      if (!locationMap.has(locationKey)) {
        locationMap.set(locationKey, {
          address: locationKey,
          units: new Set(),
          state: location.customer_state,
          city: location.customer_city,
          zipCode: location.destination_postal_code,
          serviceProvider: location.assigned_service_provider,
          resourceName: location.assigned_resource_name,
          scheduledDate: location.scheduled_date,
          status: location.order_status
        });
      }
      
      // Add order number to the location's units
      if (location.logistixai_order_number) {
        locationMap.get(locationKey).units.add(location.logistixai_order_number);
      }
    }
    
    // Add location data
    for (const [_, locationData] of locationMap.entries()) {
      for (const orderNumber of locationData.units) {
        csvContent += `${orderNumber},${locationData.scheduledDate || 'N/A'},`;
        csvContent += `"${locationData.address}","${locationData.city || ''}",${locationData.state || ''},`;
        csvContent += `${locationData.zipCode || ''},`;
        csvContent += `"${locationData.serviceProvider || 'N/A'}","${locationData.resourceName || 'N/A'}",`;
        csvContent += `${locationData.status || ''}\n`;
      }
    }
    
    return csvContent;
  } catch (error) {
    console.error('Error generating CSV export:', error);
    throw error;
  }
};

/**
 * Delete a projection and all related data
 * @param {string} projectionId - The ID of the projection to delete
 * @returns {Object} - The result of the delete operation
 */
export const deleteProjection = async (projectionId) => {
  try {
    if (!projectionId) {
      throw new Error('Projection ID is required');
    }
    
    console.log(`Deleting projection with ID: ${projectionId}`);
    
    // First, verify the user has access to this projection
    const { data: projection, error: fetchError } = await supabase
      .from('projections')
      .select('id, file_name, uploaded_by')
      .eq('id', projectionId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching projection:', fetchError);
      throw new Error('Projection not found or access denied');
    }
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user || projection.uploaded_by !== user.id) {
      throw new Error('You do not have permission to delete this projection');
    }
    
    // Delete the projection (this will cascade delete related records)
    const { error: deleteError } = await supabase
      .from('projections')
      .delete()
      .eq('id', projectionId);
    
    if (deleteError) {
      console.error('Error deleting projection:', deleteError);
      throw new Error('Failed to delete projection');
    }
    
    // Also delete any files from storage
    const { error: storageError } = await supabase.storage
      .from('projections')
      .remove([`${user.id}/${projectionId}.csv`]);
    
    if (storageError) {
      // Just log this error but don't fail the operation
      console.warn('Warning: Could not delete associated file from storage:', storageError);
    }
    
    return {
      success: true,
      message: `Projection ${projection.file_name} deleted successfully`
    };
  } catch (error) {
    console.error('Error in deleteProjection:', error);
    return {
      success: false,
      error: error.message
    };
  }
}; 